const React = require('react');
const { useEffect, useRef, useState } = require('react');
const T = require('prop-types');
const { useWatch } = require('react-hook-form');
const { analyticsTemplates } = require('utils/analytics');

const { default: Classes } = require('./styles.scss');

const BottomButtonContainer = require('components/BottomButtonContainer');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: ArrowBack } = require('@mui/icons-material/ArrowBack');
const { default: Tooltip } = require('@mui/material/Tooltip');
const Moment = require('moment/moment');

const PreventNavigationDialog = require('components/PreventNavigationDialog');
const IsEqual = require('lodash/isEqual');
const { usePrevious } = require('../../../../../hooks/usePrevious');
const { useForm } = require('hooks/useForm');
const SurveyCreatePageFormConfig = require('./formConfig');

const SurveyCreatePage = (props) => {

    const {
        survey,
        showNotification,
        goBack,
        onSubmit,
        onClickResetSearchFilters,
        activeSearchFilter,
        interestsActiveFilter_survey,
        badges,
        departments,
        yearsHiredList,
        officesList,
        isCompany,
        isCommunity,
        isSchoolOnline,
        schoolRoles,
        schoolRoleGroups,
        searchResults,
        history,
        majors
    } = props;

    const [csv, setCsv] = useState(null);

    const notifySubmitErrors = (submitErrors = {}) => {

        const flattenedErrors = SurveyCreatePageFormConfig.flattenErrors(submitErrors);
        const visibleErrors = flattenedErrors.slice(0, 4);

        console.warn('[SurveyCreatePage] submit validation failed', {
            errors: submitErrors,
            flattenedErrors
        });

        if (!visibleErrors.length) {
            showNotification('Please correct errors above');
            return;
        }

        const summary = visibleErrors
            .map(({ label, message }) => `${label}: ${message}`)
            .join('; ');

        const suffix = flattenedErrors.length > visibleErrors.length
            ? `; +${flattenedErrors.length - visibleErrors.length} more`
            : '';

        showNotification(`Please correct: ${summary}${suffix}`);
    };

    const onSubmitForm = (data) => {

        analyticsTemplates.buttons('save new survey', 'surveys: create new survey request');
        const payload = SurveyCreatePageFormConfig.buildSubmitPayload(data, { csv });

        if (payload.selectedRole === 'search' && activeSearchFilter && Object.keys(activeSearchFilter).length) {
            const selectedIds = new Set(payload.selectedUsers.map(Number));

            payload.usedCriteria = activeSearchFilter;
            payload.excludedUserIds = searchResults
                .map(({ id }) => Number(id))
                .filter((id) => !selectedIds.has(id));
        }

        onSubmit(payload);
    };

    const {
        methods,
        Form
    } = useForm(SurveyCreatePageFormConfig, {
        activeSearchFilter,
        badges,
        csv,
        departments,
        interestsActiveFilter_survey,
        isCompany,
        isCommunity,
        isSchoolOnline,
        majors,
        officesList,
        onClickResetSearchFilters,
        onSubmit: onSubmitForm,
        schoolRoleGroups,
        schoolRoles,
        searchResults,
        setCsv,
        yearsHiredList
    });

    const {
        control,
        handleSubmit,
        formState: {
            isDirty,
            isValid,
            isSubmitting,
            isSubmitSuccessful
        },
        setValue
    } = methods;

    const watchStartDate = useWatch({ control, name: 'startDate' });
    const watchEndDate = useWatch({ control, name: 'endDate' });
    const manuallyStopVal = useWatch({ control, name: 'manuallyStop' });
    const title = useWatch({ control, name: 'title' });
    const selectedRole = SurveyCreatePageFormConfig.normalizeSelectedRole(useWatch({ control, name: 'selectedRole' }));
    const selectedUsers = useWatch({ control, name: 'selectedUsers' }) ?? [];
    const questions = useWatch({ control, name: 'questions' }) ?? [];

    const hasRequiredDataForSubmit = SurveyCreatePageFormConfig.isSubmittable({
        csv,
        questions,
        selectedRole,
        selectedUsers,
        title
    });

    const prevSearchResult = usePrevious(searchResults);

    useEffect(() => {

        if (Moment(watchStartDate).isAfter(Moment(watchEndDate)) && !manuallyStopVal) {
            setValue('endDate', Moment(watchStartDate).add(1, 'days').format('YYYY-MM-DD'), {
                shouldDirty: true,
                shouldValidate: true
            });
        }
    }, [manuallyStopVal, setValue, watchEndDate, watchStartDate]);

    useEffect(() => {

        if (searchResults !== null && !IsEqual(searchResults, prevSearchResult)) {
            setValue('selectedUsers', searchResults, {
                shouldDirty: true,
                shouldValidate: true
            });
        }
    }, [prevSearchResult, searchResults, setValue]);

    const isInitialized = useRef(false);

    useEffect(() => {

        if (survey?.questions?.length > 0 && !isInitialized.current) {
            setValue('title', `Copy of ${survey.title}`, {
                shouldDirty: true,
                shouldValidate: true
            });
            setValue('description', survey.description ?? '', {
                shouldDirty: true,
                shouldValidate: true
            });
            setValue('questions', SurveyCreatePageFormConfig.buildCopyQuestions(survey), {
                shouldDirty: true,
                shouldValidate: true
            });
            isInitialized.current = true;
        }
    }, [survey, setValue]);

    return (
        <BottomButtonContainer
            btnLabel='Save'
            formId={SurveyCreatePageFormConfig.formId}
            disabled={!hasRequiredDataForSubmit || isSubmitting || isSubmitSuccessful}
        >
            <PreventNavigationDialog
                saveFunction={handleSubmit(
                    onSubmitForm,
                    notifySubmitErrors
                )}
                alertMsg={'You have made changes.\n Do you want to save or discard them?'}
                when={isDirty && !isSubmitSuccessful}
                navigate={(path, state) => history.push(path, state)}
                shouldBlockNavigation={() => {

                    if ((isDirty || !isValid) && !isSubmitSuccessful) {
                        return true;
                    }

                    return false;
                }}
                pageHasErrors={!isValid}
            />
            <div className={Classes.wrapper}>
                <div className={Classes.innerWrapper}>
                    <div className={Classes.topRow}>
                        <Tooltip title={'Go Back'} arrow={true} placement={'top'}>
                            <IconButton onClick={goBack} aria-label='previous screen' size='large'><ArrowBack /></IconButton>
                        </Tooltip>

                        <h2>New Survey</h2>
                    </div>
                    {Form}
                </div>
            </div>
        </BottomButtonContainer>
    );
};

SurveyCreatePage.propTypes = {
    survey: T.object,
    showNotification: T.func,
    goBack: T.func,
    onSubmit: T.func,
    onClickResetSearchFilters: T.func,
    activeSearchFilter: T.object,
    interestsActiveFilter_survey: T.func,
    badges: T.array,
    searchResults: T.array,
    departments: T.array,
    officesList: T.arrayOf(
        T.shape({
            id: T.number,
            name: T.string
        })
    ),
    yearsHiredList: T.arrayOf(
        T.shape({
            id: T.number,
            year: T.number
        })
    ),
    isCompany: T.bool,
    isCommunity: T.bool,
    isSchoolOnline: T.bool,
    schoolRoles: T.array,
    schoolRoleGroups: T.array,
    history: T.object,
    majors: T.arrayOf(T.shape({
        id: T.number,
        name: T.string
    }))
};

module.exports = SurveyCreatePage;
